import { action, createAsyncAction } from 'typesafe-actions'
import {
  ManagePlayerModalState,
  ManagePlayersInWatchlistsOperation,
  Player,
  Sorting,
  Watchlist,
  WatchlistSortingKeys,
} from 'types/global'
import api from 'api/graphql/graphql'
import { toast } from 'react-toastify'
import i18n from 'constants/strings'
import { WatchlistInput } from 'types/api/types'
import { pushNotification } from './notifications-actions'

export const SET_SORTING = 'watchlist/SET_SORTING'
export const SET_MANAGE_MODAL_STATE = 'watchlist/SET_MANAGE_MODAL_STATE'
export const SET_DESTINATION_WATCHLIST_IDS = 'watchlist/SET_DESTINATION_WATCHLIST_IDS'
export const ADD_DESTINATION_WATCHLIST_IDS = 'watchlist/ADD_DESTINATION_WATCHLIST_IDS'
export const MANAGE_PLAYER_STARTED = 'watchlist/MANAGE_PLAYER_STARTED'
export const MANAGE_PLAYER_COMPLETED = 'watchlist/MANAGE_PLAYER_COMPLETED'

export const WATCHLIST_PLAYERS_REQUESTED = 'watchlist/WATCHLIST_PLAYERS_REQUESTED'
export const WATCHLIST_PLAYERS_COMPLETED = 'watchlist/WATCHLIST_PLAYERS_COMPLETED'
export const WATCHLIST_PLAYERS_FAILED = 'watchlist/WATCHLIST_PLAYERS_FAILED'

export const requestWatchlistPlayers = createAsyncAction(
  WATCHLIST_PLAYERS_REQUESTED,
  WATCHLIST_PLAYERS_COMPLETED,
  WATCHLIST_PLAYERS_FAILED
)<void, { id: string; players: Player[] }, Error>()

export const RESET_WATCHLIST_PLAYERS = 'watchlist/RESET_WATCHLIST_PLAYERS'

export const FETCH_WATCHLISTS_REQUESTED = 'watchlist/FETCH_WATCHLISTS_REQUESTED'
export const FETCH_WATCHLISTS_COMPLETED = 'watchlist/FETCH_WATCHLISTS_COMPLETED'
export const FETCH_WATCHLISTS_FAILED = 'watchlist/FETCH_WATCHLISTS_FAILED'

export const requestWatchlists = createAsyncAction(
  FETCH_WATCHLISTS_REQUESTED,
  FETCH_WATCHLISTS_COMPLETED,
  FETCH_WATCHLISTS_FAILED
)<void, Watchlist[], Error>()

export const UPDATE_WATCHLIST_REQUESTED = 'watchlist/UPDATE_WATCHLIST_REQUESTED'
export const UPDATE_WATCHLIST_COMPLETED = 'watchlist/UPDATE_WATCHLIST_COMPLETED'
export const UPDATE_WATCHLIST_FAILED = 'watchlist/UPDATE_WATCHLIST_FAILED'

export const requestUpdateWatchlist = createAsyncAction(
  UPDATE_WATCHLIST_REQUESTED,
  UPDATE_WATCHLIST_COMPLETED,
  UPDATE_WATCHLIST_FAILED
)<void, Watchlist, Error>()

export const ADD_WATCHLIST_REQUESTED = 'watchlist/ADD_WATCHLIST_REQUESTED'
export const ADD_WATCHLIST_COMPLETED = 'watchlist/ADD_WATCHLIST_COMPLETED'
export const ADD_WATCHLIST_FAILED = 'watchlist/ADD_WATCHLIST_FAILED'

export const requestAddWatchlist = createAsyncAction(
  ADD_WATCHLIST_REQUESTED,
  ADD_WATCHLIST_COMPLETED,
  ADD_WATCHLIST_FAILED
)<void, Watchlist, Error>()

export const REMOVE_WATCHLIST_REQUESTED = 'watchlist/REMOVE_WATCHLIST_REQUESTED'
export const REMOVE_WATCHLIST_COMPLETED = 'watchlist/REMOVE_WATCHLIST_COMPLETED'
export const REMOVE_WATCHLIST_FAILED = 'watchlist/REMOVE_WATCHLIST_FAILED'

export const requestRemoveWatchlist = createAsyncAction(
  REMOVE_WATCHLIST_REQUESTED,
  REMOVE_WATCHLIST_COMPLETED,
  REMOVE_WATCHLIST_FAILED
)<void, string, Error>()

export const getWatchlists = () => {
  return async dispatch => {
    try {
      dispatch(requestWatchlists.request())
      dispatch(requestWatchlists.success(await api.getWatchlists()))
    } catch (e) {
      console.warn('Failed to get watchlists', e && (e as Error)?.message)
      dispatch(requestWatchlists.failure(e as Error))
    }
  }
}

export const getWatchlistPlayers = (watchlist: Watchlist) => {
  return async dispatch => {
    try {
      dispatch(requestWatchlistPlayers.request())
      return dispatch(
        requestWatchlistPlayers.success({
          players: watchlist.playerIds.length
            ? (await api.getWatchlistWithPlayers(watchlist.id)).players
            : [],
          id: watchlist.id,
        })
      )
    } catch (e) {
      console.log('Could not fetch watchlist players', e)
      return dispatch(requestWatchlistPlayers.failure(e as Error))
    }
  }
}

export const resetWatchlistPlayers = (watchlistId: string) =>
  action(RESET_WATCHLIST_PLAYERS, { watchlistId })

export const updateWatchlist = (watchlistId: string, updatedFields: { name: string }) => {
  return async dispatch => {
    dispatch(requestUpdateWatchlist.request())
    try {
      dispatch(
        requestUpdateWatchlist.success(
          await toast.promise(api.renameWatchlist(watchlistId, updatedFields.name), {
            pending: i18n.t('strAlerts.renamingWl'),
            error: i18n.t('strAlerts.errorRenamingWl'),
            success: i18n.t('strAlerts.wlRenamed'),
          })
        )
      )
    } catch (e) {
      console.warn(`Failed to update watchlist ${watchlistId}`, e && (e as Error)?.message)
      dispatch(requestUpdateWatchlist.failure(e as Error))
    }
  }
}

export const addPlayerToWatchlists = (playerId: string, watchlistIds: string[]) => {
  return async dispatch => {
    try {
      await toast.promise(api.addPlayersToWatchlists(watchlistIds, [playerId]), {
        pending: i18n.t('strAlerts.addingPlayerWl'),
        error: i18n.t('strAlerts.errorAddingPlayerWl'),
        success: i18n.t('strAlerts.playerWlAdded'),
      })
      dispatch(getWatchlists())
    } catch (e) {
      console.log(e && (e as Error)?.message)
    }
  }
}

export const removeWatchlistIdsForPlayer = (playerId: string, watchlistIds: string[]) => {
  return async dispatch => {
    try {
      await toast.promise(api.removePlayersFromWatchlists(watchlistIds, [playerId]), {
        pending: i18n.t('strAlerts.removingPlayerWl'),
        error: i18n.t('strAlerts.errorRemovingPlayerWl'),
        success: i18n.t('strAlerts.playerWlRemoved'),
      })
      dispatch(getWatchlists())
    } catch (e) {
      console.log(e && (e as Error)?.message)
    }
  }
}

export const managePlayerInWatchlists = (
  player: Player,
  input: { addToIds?: string[]; removeFromIds?: string[]; createWatchlistName?: string },
  options?: {
    addCreatedToDestinationWatchlists: boolean
  }
) => {
  return async dispatch => {
    let successfullOperationsTypes: ManagePlayersInWatchlistsOperation[] = []
    const operationsRecord: Record<
      ManagePlayersInWatchlistsOperation,
      { requested: string[]; successful: string[] }
    > = {
      add: { requested: input?.addToIds ?? [], successful: [] },
      remove: { requested: input?.removeFromIds ?? [], successful: [] },
      create: {
        // as we do not have the id of a watchlist that has not been created yet, we use the name as a reference
        requested: input?.createWatchlistName ? [input.createWatchlistName] : [],
        successful: [],
      },
    }
    dispatch(setManagePlayerStarted(player.id))
    try {
      const results = await api.managePlayersInWatchlists([player.id], input)
      dispatch(setManagePlayerCompleted(player.id))
      dispatch(getWatchlists())

      results.forEach(result => {
        if (result.successful && result.updatedWatchlistsIds.length) {
          successfullOperationsTypes.push(result.type)
          operationsRecord[result.type].successful = result.updatedWatchlistsIds
        }
      })

      if (options?.addCreatedToDestinationWatchlists && operationsRecord.create.successful.length) {
        dispatch(addDestinationWatchlistIds(operationsRecord.create.successful))
      }

      if (successfullOperationsTypes.length) {
        dispatch(
          pushNotification({
            notification: {
              type: 'success',
              context: {
                player: { ...player },
                operationTypes: successfullOperationsTypes,
                interestedWatchlistRefs: successfullOperationsTypes.reduce(
                  (acc, current) => acc.concat(operationsRecord[current].successful),
                  [] as string[]
                ),
              },
            },
            key: 'watchlists',
          })
        )
      }

      for (let key in operationsRecord) {
        const { requested, successful } =
          operationsRecord[key as ManagePlayersInWatchlistsOperation]

        if (requested.length > successful.length) {
          let failedWatchlistIds = requested

          if (successful.length) {
            const successfulSet = new Set(successful)
            failedWatchlistIds = requested.filter(x => !successfulSet.has(x))
          }

          dispatch(
            pushNotification({
              notification: {
                type: 'error',
                context: {
                  player: { ...player },
                  operationTypes: [key],
                  interestedWatchlistRefs: failedWatchlistIds,
                },
              },
              key: 'watchlists',
            })
          )
        }
      }
    } catch (e) {
      console.warn(e && (e as Error)?.message)
    }
  }
}

export const addWatchlist = (fields: WatchlistInput) => {
  return async dispatch => {
    try {
      dispatch(requestAddWatchlist.request())
      dispatch(
        requestAddWatchlist.success(
          await toast.promise(api.addWatchlist(fields), {
            pending: i18n.t('strAlerts.creatingWl'),
            error: i18n.t('strAlerts.errorCreatingWl'),
            success: i18n.t('strAlerts.wlCreated'),
          })
        )
      )
    } catch (e) {
      console.warn('Failed to add watchlist', e && (e as Error)?.message)
      dispatch(requestAddWatchlist.failure(e as Error))
    }
  }
}

export const deleteWatchlist = (watchlistId: string) => {
  return async dispatch => {
    try {
      dispatch(requestRemoveWatchlist.request())
      await toast.promise(api.removeWatchlist(watchlistId), {
        pending: i18n.t('strAlerts.deletingWl'),
        error: i18n.t('strAlerts.errorDeletingWl'),
        success: i18n.t('strAlerts.wlDeleted'),
      })
      dispatch(requestRemoveWatchlist.success(watchlistId))
    } catch (e) {
      console.warn(e && (e as Error)?.message)
      dispatch(requestRemoveWatchlist.failure(e as Error))
    }
  }
}

export const setSorting = (sorting: Sorting, watchlistSortingId: WatchlistSortingKeys) =>
  action(SET_SORTING, { sorting, watchlistSortingId })

export const setManageModalState = (modalState: Partial<ManagePlayerModalState>) =>
  action(SET_MANAGE_MODAL_STATE, modalState)

export const setDestinationWatchlistIds = (ids: string[]) =>
  action(SET_DESTINATION_WATCHLIST_IDS, ids)

export const addDestinationWatchlistIds = (ids: string[]) =>
  action(ADD_DESTINATION_WATCHLIST_IDS, ids)

export const setManagePlayerStarted = (id: string) => action(MANAGE_PLAYER_STARTED, id)

export const setManagePlayerCompleted = (id: string) => action(MANAGE_PLAYER_COMPLETED, id)
